import React from 'react';
import { Link } from 'react-router-dom';
import Button from '../../components/ui/Button';

const Landing = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10">
          <div className="text-center animate-fade-in">
            <div className="floating-element mb-8">
              <div className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-md rounded-full text-primary-black font-semibold">
                <span className="w-2 h-2 bg-primary-lightGreen rounded-full mr-2 animate-pulse"></span>
                New jobs posted daily
              </div>
            </div>
            
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold mb-4 md:mb-6 gradient-text leading-tight">
              Find Your Dream Job
            </h1>
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl mb-6 md:mb-8 text-primary-black/80 max-w-3xl mx-auto leading-relaxed px-4">
              Connect with top companies and discover amazing opportunities with our green-powered platform
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-8 md:mb-12 px-4">
              <Link to="/register" className="w-full sm:w-auto">
                <Button className="btn-primary text-base sm:text-lg px-6 sm:px-8 py-3 sm:py-4 w-full sm:w-auto">
                  Get Started Free
                </Button>
              </Link>
              <Link to="/register" className="w-full sm:w-auto">
                <Button className="btn-outline text-base sm:text-lg px-6 sm:px-8 py-3 sm:py-4 w-full sm:w-auto">
                  Browse Jobs
                </Button>
              </Link>
            </div>
            
            {/* Trust indicators */}
            <div className="flex flex-wrap justify-center items-center gap-4 sm:gap-6 md:gap-8 text-xs sm:text-sm text-primary-black/60 px-4">
              <div className="flex items-center">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-primary-lightGreen mr-1 sm:mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Trusted by 10K+ users
              </div>
              <div className="flex items-center">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-primary-lightGreen mr-1 sm:mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Free to use
              </div>
              <div className="flex items-center">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-primary-lightGreen mr-1 sm:mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                No hidden fees
              </div>
            </div>
          </div>
        </div>
        
        {/* Floating elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-primary-lightGreen/20 rounded-full floating-element"></div>
        <div className="absolute top-40 right-20 w-16 h-16 bg-primary-teaGreen/30 rounded-full floating-element" style={{animationDelay: '2s'}}></div>
        <div className="absolute bottom-20 left-20 w-12 h-12 bg-primary-lightGreen/25 rounded-full floating-element" style={{animationDelay: '4s'}}></div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-gradient-to-br from-primary-teaGreen/5 to-primary-lightGreen/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-20 animate-slide-up px-4">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-primary-black mb-4 md:mb-6">
              Why Choose <span className="gradient-text">Dhoondh</span>?
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              We make job hunting and hiring simple, effective, and environmentally conscious
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            <div className="feature-card group animate-fade-in px-4 md:px-0" style={{animationDelay: '0.1s'}}>
              <div className="w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-primary-lightGreen to-primary-teaGreen rounded-2xl flex items-center justify-center mx-auto mb-4 md:mb-6 group-hover:scale-110 transition-transform duration-300">
                <svg className="h-8 w-8 md:h-10 md:w-10 text-primary-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2V6" />
                </svg>
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-primary-black mb-3 md:mb-4">For Job Seekers</h3>
              <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                Find opportunities that match your skills and interests. Connect with top companies and build your career with our intelligent matching system.
              </p>
            </div>

            <div className="feature-card group animate-fade-in px-4 md:px-0" style={{animationDelay: '0.2s'}}>
              <div className="w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-primary-lightGreen to-primary-teaGreen rounded-2xl flex items-center justify-center mx-auto mb-4 md:mb-6 group-hover:scale-110 transition-transform duration-300">
                <svg className="h-8 w-8 md:h-10 md:w-10 text-primary-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-primary-black mb-3 md:mb-4">For Companies</h3>
              <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                Post jobs and find qualified candidates. Access a pool of talented professionals and streamline your hiring process with our advanced tools.
              </p>
            </div>

            <div className="feature-card group animate-fade-in px-4 md:px-0" style={{animationDelay: '0.3s'}}>
              <div className="w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-primary-lightGreen to-primary-teaGreen rounded-2xl flex items-center justify-center mx-auto mb-4 md:mb-6 group-hover:scale-110 transition-transform duration-300">
                <svg className="h-8 w-8 md:h-10 md:w-10 text-primary-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-primary-black mb-3 md:mb-4">For Agents</h3>
              <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                Manage applications and help companies find the right talent. Our comprehensive dashboard makes recruitment management effortless.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
            <div className="stats-card animate-fade-in text-center px-2" style={{animationDelay: '0.1s'}}>
              <div className="text-3xl md:text-5xl font-bold text-primary-black mb-1 md:mb-2">10K+</div>
              <div className="text-xs md:text-base text-gray-600 font-medium">Active Users</div>
              <div className="w-12 md:w-16 h-1 bg-primary-lightGreen rounded-full mx-auto mt-2 md:mt-4"></div>
            </div>
            <div className="stats-card animate-fade-in text-center px-2" style={{animationDelay: '0.2s'}}>
              <div className="text-3xl md:text-5xl font-bold text-primary-black mb-1 md:mb-2">5K+</div>
              <div className="text-xs md:text-base text-gray-600 font-medium">Job Postings</div>
              <div className="w-12 md:w-16 h-1 bg-primary-lightGreen rounded-full mx-auto mt-2 md:mt-4"></div>
            </div>
            <div className="stats-card animate-fade-in text-center px-2" style={{animationDelay: '0.3s'}}>
              <div className="text-3xl md:text-5xl font-bold text-primary-black mb-1 md:mb-2">2K+</div>
              <div className="text-xs md:text-base text-gray-600 font-medium">Companies</div>
              <div className="w-12 md:w-16 h-1 bg-primary-lightGreen rounded-full mx-auto mt-2 md:mt-4"></div>
            </div>
            <div className="stats-card animate-fade-in text-center px-2" style={{animationDelay: '0.4s'}}>
              <div className="text-3xl md:text-5xl font-bold text-primary-black mb-1 md:mb-2">95%</div>
              <div className="text-xs md:text-base text-gray-600 font-medium">Success Rate</div>
              <div className="w-12 md:w-16 h-1 bg-primary-lightGreen rounded-full mx-auto mt-2 md:mt-4"></div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-24 bg-gradient-to-r from-primary-black to-primary-lightGreen text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 md:mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-base sm:text-lg md:text-xl mb-8 md:mb-12 text-white/90 max-w-3xl mx-auto px-4">
            Join thousands of professionals and companies already using Dhoondh to transform their hiring process
          </p>
          <div className="flex flex-col sm:flex-row gap-4 md:gap-6 justify-center px-4">
            <Link to="/register" className="w-full sm:w-auto">
              <Button className="border-2 border-white text-white bg-transparent/20 backdrop-blur-sm hover:bg-white hover:text-primary-black text-base md:text-lg px-6 md:px-8 py-3 md:py-4 font-bold w-full sm:w-auto">
                Sign Up as Job Seeker
              </Button>
            </Link>
            <Link to="/company/register?isCompany=true" className="w-full sm:w-auto">
              <Button className="border-2 border-white text-white bg-transparent/20 backdrop-blur-sm hover:bg-white hover:text-primary-black text-base md:text-lg px-6 md:px-8 py-3 md:py-4 font-bold w-full sm:w-auto">
                Register Company
              </Button>
            </Link>
          </div>
          
          <div className="mt-8 md:mt-12 text-white/70 px-4">
            <p className="text-sm md:text-base">Already have an account? <Link to="/login" className="text-white font-semibold hover:underline">Sign in here</Link></p>
          </div>
        </div>
        
        {/* Background decoration */}
        <div className="absolute top-10 left-10 w-32 h-32 bg-white/10 rounded-full floating-element"></div>
        <div className="absolute bottom-10 right-10 w-24 h-24 bg-white/10 rounded-full floating-element" style={{animationDelay: '3s'}}></div>
      </section>
    </div>
  );
};

export default Landing;
